import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Folder, Home, Check, ChevronRight } from 'lucide-react';
import { Modal, Button, cn } from '@ai-tutor/ui';
import { folderApi, type FolderItem } from '@/lib/api-client';

interface MoveToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the target folder UUID, or null for root. */
  onSelect: (folderId: string | null) => Promise<void> | void;
  /** Currently-assigned folder - highlighted and disabled. */
  currentFolderId?: string | null;
  /**
   * When moving a folder, we must exclude itself + its descendants
   * from the picker to avoid cycles.
   */
  excludeFolderIds?: string[];
  title?: string;
}

interface TreeNode {
  folder: FolderItem;
  children: TreeNode[];
  depth: number;
}

function buildTree(folders: FolderItem[]): TreeNode[] {
  const byParent = new Map<string | null, FolderItem[]>();
  for (const f of folders) {
    const key = f.parent_folder_id;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(f);
  }
  const walk = (parentId: string | null, depth: number): TreeNode[] =>
    (byParent.get(parentId) ?? [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(f => ({ folder: f, children: walk(f.folder_id, depth + 1), depth }));
  return walk(null, 0);
}

function flatten(tree: TreeNode[]): TreeNode[] {
  const out: TreeNode[] = [];
  const stack = [...tree];
  while (stack.length) {
    const node = stack.shift()!;
    out.push(node);
    if (node.children.length) stack.unshift(...node.children);
  }
  return out;
}

export function MoveToFolderModal({
  isOpen,
  onClose,
  onSelect,
  currentFolderId,
  excludeFolderIds = [],
  title = 'Move to folder',
}: MoveToFolderModalProps) {
  const [selected, setSelected] = useState<string | null | undefined>(
    undefined
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(undefined);
      setSubmitting(false);
    }
  }, [isOpen]);

  const { data, isLoading } = useQuery({
    queryKey: ['folders', 'all'],
    queryFn: () => folderApi.listFolders(),
    enabled: isOpen,
    staleTime: 30000,
  });

  const nodes = useMemo(() => {
    const folders = data?.folders ?? [];
    const excluded = new Set(excludeFolderIds);
    // Also exclude descendants of anything in the exclude set.
    const byId = new Map(folders.map(f => [f.folder_id, f]));
    const isExcluded = (id: string): boolean => {
      let cursor: string | null = id;
      while (cursor) {
        if (excluded.has(cursor)) return true;
        const f = byId.get(cursor);
        cursor = f?.parent_folder_id ?? null;
      }
      return false;
    };
    const filtered = folders.filter(f => !isExcluded(f.folder_id));
    return flatten(buildTree(filtered));
  }, [data, excludeFolderIds]);

  const handleConfirm = async () => {
    if (selected === undefined) return;
    setSubmitting(true);
    try {
      await onSelect(selected);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size='md'>
      <div className='flex max-h-[60vh] flex-col'>
        <div className='flex-1 overflow-y-auto px-2 py-2'>
          {/* Root option */}
          <button
            onClick={() => setSelected(null)}
            disabled={currentFolderId == null}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors max-sm:min-h-11',
              'hover:bg-muted',
              selected === null && 'bg-blue-50 dark:bg-tint-brand',
              currentFolderId == null && 'cursor-not-allowed opacity-60'
            )}
          >
            <Home className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
            <span className='flex-1 text-foreground'>My Learning (root)</span>
            {currentFolderId == null && (
              <span className='text-xs text-muted-foreground'>Current</span>
            )}
            {selected === null && (
              <Check className='h-4 w-4 text-blue-600 dark:text-primary' />
            )}
          </button>

          {isLoading && (
            <div className='px-3 py-4 text-sm text-muted-foreground'>
              Loading folders…
            </div>
          )}

          {!isLoading && nodes.length === 0 && (
            <div className='px-3 py-4 text-sm text-muted-foreground'>
              No other folders yet. Create one from the "+ New folder" button.
            </div>
          )}

          {nodes.map(({ folder, depth }) => {
            const isCurrent = folder.folder_id === currentFolderId;
            const isSelected = selected === folder.folder_id;
            return (
              <button
                key={folder.folder_id}
                onClick={() => setSelected(folder.folder_id)}
                disabled={isCurrent}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors max-sm:min-h-11',
                  'hover:bg-muted',
                  isSelected && 'bg-blue-50 dark:bg-tint-brand',
                  isCurrent && 'cursor-not-allowed opacity-60'
                )}
                style={{ paddingLeft: `${12 + depth * 20}px` }}
              >
                {depth > 0 && (
                  <ChevronRight className='h-3 w-3 flex-shrink-0 text-muted-foreground' />
                )}
                <Folder className='h-4 w-4 flex-shrink-0 text-brand-600 dark:text-primary' />
                <span className='flex-1 truncate text-foreground'>
                  {folder.name}
                </span>
                {isCurrent && (
                  <span className='text-xs text-muted-foreground'>Current</span>
                )}
                {isSelected && (
                  <Check className='h-4 w-4 text-blue-600 dark:text-primary' />
                )}
              </button>
            );
          })}
        </div>

        <div className='flex justify-end gap-2 border-t border-border px-6 py-4 max-sm:px-4 max-sm:[&>button]:h-11 max-sm:[&>button]:flex-1'>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selected === undefined || submitting}
          >
            {submitting ? 'Moving…' : 'Move here'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
